import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { z } from 'zod'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { DataTable } from '@/components/ui/data-table'
import { EyeIcon, SearchLgIcon } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import { EmptyStateListings } from '@/features/luxe-submissions/components/empty-state-listings'
import { FilterLocationSelect } from '@/features/luxe-submissions/components/filter-location-select'
import { InventoryFilterSelect } from '@/features/luxe-submissions/components/inventory-filter-select'
import { useGetMMSubmissions } from '@/features/luxe-submissions/queries/use-get-mm-submissions'
import {
	type MMSubmission,
	MMSubmissionInventory
} from '@/features/luxe-submissions/types'
import {
	mmInventoryToBadgeStyle,
	mmInventoryToLabelTKey
} from '@/features/luxe-submissions/utils'
import type { Order, OrderDirection } from '@/helpers/types'
import { cn, debounce } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useRowsPerPage } from '@/hooks/use-rows-per-page'
import { routes } from '@/routes'
import { useTranslation } from '@/translations/i18n'

const MMSubmissionsSearchParamsSchema = z.object({
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	location: z.string().optional(),
	listingInventory: MMSubmissionInventory.optional(),
	search: z.string().optional(),
	page: z.string().optional(),
	perPage: z.string().optional()
})

type SortColumn = 'listingId' | 'location' | 'listingInventory'

function LuxeSubmissionsMMSubmissions() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MMSubmissionsSearchParamsSchema
	)

	const currentPage = searchParams.page ? Number(searchParams.page) : 1

	const { rowsPerPage, setRowsPerPage, totalRows, reportTotal } =
		useRowsPerPage({
			searchParam: searchParams.perPage,
			setSearchParam: (newPerPage) =>
				setSearchParams({ perPage: newPerPage, page: undefined })
		})

	// Queries
	const { data: listingsData, isLoading: isLoadingListings } =
		useGetMMSubmissions({
			page: currentPage,
			perPage: rowsPerPage,
			sortBy: searchParams.sortBy,
			sortOrder: searchParams.sortOrder,
			location: searchParams.location,
			listingInventory: searchParams.listingInventory,
			search: searchParams.search
		})

	// Pagination
	const lastPage = listingsData ? listingsData.meta.lastPage : undefined
	reportTotal(listingsData?.meta.total)

	const setPage = (page: number) => {
		setSearchParams({
			page: page === 1 ? undefined : String(page)
		})
	}

	// Filters
	const handleLocationChange = (location: string | null) => {
		setSearchParams({
			location: location ?? undefined,
			page: undefined
		})
	}

	const handleInventoryChange = (inventory: string | null) => {
		setSearchParams({
			listingInventory: (inventory as MMSubmissionInventory) ?? undefined,
			page: undefined
		})
	}

	// Debounced search by ID
	const debouncedSearchId = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({
					search: value || undefined,
					page: undefined
				})
			}, 300),
		[setSearchParams]
	)

	// Navigate to listing details
	const handleViewListing = useCallback(
		(listingId: string) => {
			navigate(
				routes.LUXE_SUBMISSIONS_MM_SUBMISSION_DETAILS.handle.fullPath.replace(
					':id',
					listingId
				),
				{ state: { fromSearch: location.search } }
			)
		},
		[navigate, location.search]
	)

	// Order state for DataTable
	const order = useMemo((): Order<SortColumn> | undefined => {
		if (!searchParams.sortBy) {
			return undefined
		}
		return {
			orderBy: searchParams.sortBy as SortColumn,
			orderDirection: searchParams.sortOrder as OrderDirection | undefined
		}
	}, [searchParams.sortBy, searchParams.sortOrder])

	const setOrder = (newOrder?: Order<SortColumn>) => {
		setSearchParams({
			sortBy: newOrder?.orderBy,
			sortOrder: newOrder?.orderDirection,
			page: undefined
		})
	}

	const hasFilters = Object.values(searchParams).some(Boolean)

	// Table columns
	const columns = useMemo(
		() => [
			{
				id: 'listingId' as const,
				header: t('luxe_submissions:listing_id_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<div className='flex flex-col'>
						<a
							className={cn(
								'truncate font-medium text-neutral-900 text-sm',
								dataItem.listingUrl && 'underline'
							)}
							href={dataItem.listingUrl ?? undefined}
							rel='noopener noreferrer'
							target='_blank'
							title={dataItem.airbnbListingId ?? dataItem.id}
						>
							#{dataItem.airbnbListingId ?? dataItem.id}
						</a>

						{dataItem.listingName && (
							<span className='text-neutral-600 text-sm'>
								{dataItem.listingName}
							</span>
						)}
					</div>
				),
				size: 240,
				sortKey: 'listingId' as const
			},
			{
				id: 'hostInfo' as const,
				header: t('luxe_submissions:host_info_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<div className='flex flex-col'>
						{dataItem.externalHostId && (
							<span
								className={cn(
									'font-medium text-neutral-900 text-sm'
								)}
							>
								#{dataItem.externalHostId}
							</span>
						)}
						{dataItem.hostEmail && (
							<span
								className='truncate text-neutral-600 text-sm'
								title={dataItem.hostEmail}
							>
								{dataItem.hostEmail}
							</span>
						)}
					</div>
				),
				size: 240
			},
			{
				id: 'location' as const,
				header: t('luxe_submissions:location_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) => {
					return dataItem.location ? (
						<span className='text-neutral-700 text-sm'>
							{dataItem.location}
						</span>
					) : (
						<span className='text-neutral-500 text-sm'>—</span>
					)
				},
				size: 160,
				sortKey: 'location' as const
			},
			{
				id: 'listingInventory' as const,
				header: t('luxe_submissions:inventory_column'),
				cell: ({ dataItem }: { dataItem: MMSubmission }) =>
					dataItem.listingInventory ? (
						<span
							className={`inline-flex rounded-full border px-2 py-0.5 font-medium text-xs ${mmInventoryToBadgeStyle[dataItem.listingInventory]}`}
						>
							{t(
								mmInventoryToLabelTKey[
									dataItem.listingInventory
								]
							)}
						</span>
					) : null,
				size: 260,
				sortKey: 'listingInventory' as const
			},
			{
				id: 'action' as const,
				header: '',
				cell: ({ dataItem }: { dataItem: MMSubmission }) => (
					<button
						className='flex w-full cursor-pointer items-center justify-end text-neutral-500 hover:text-neutral-700'
						onClick={() => handleViewListing(dataItem.id)}
						type='button'
					>
						<EyeIcon size={20} />
					</button>
				),
				size: 60
			}
		],
		[t, handleViewListing]
	)

	return (
		<div className='flex flex-1 flex-col overflow-hidden px-10'>
			{/* Filters */}
			<div className='flex shrink-0 items-center justify-between py-6'>
				<div className='flex items-center gap-3'>
					<FilterLocationSelect
						onChange={handleLocationChange}
						selectedId={searchParams.location ?? null}
					/>

					<InventoryFilterSelect
						onChange={handleInventoryChange}
						value={searchParams.listingInventory ?? null}
					/>
				</div>
				<div className='relative w-[320px]'>
					<SearchLgIcon
						className='pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-neutral-500'
						size={20}
					/>
					<Input
						className='h-9 w-full rounded-lg border-neutral-300 pl-10 text-sm shadow-xs placeholder:text-neutral-500'
						defaultValue={searchParams.search ?? ''}
						onChange={(e) => debouncedSearchId(e.target.value)}
						placeholder={t(
							'luxe_submissions:search_by_listing_id_email'
						)}
					/>
				</div>
			</div>

			{/* Table */}
			<div className='flex-1 overflow-hidden'>
				<DataTable
					columnPinning={{
						left: [],
						right: ['action']
					}}
					columns={columns}
					data={listingsData?.data ?? []}
					emptyComponent={
						<EmptyStateListings
							action={
								hasFilters ? null : (
									<ButtonLegacy
										onClick={() => window.location.reload()}
										variant='secondary'
									>
										{t('common:refresh')}
									</ButtonLegacy>
								)
							}
							description={t(
								hasFilters
									? 'common:empty_table_description'
									: 'luxe_submissions:mm_submissions_no_listings_description'
							)}
							title={t(
								hasFilters
									? 'common:empty_table_title'
									: 'luxe_submissions:mm_submissions_no_listings_title'
							)}
						/>
					}
					isLoading={isLoadingListings}
					lastPage={lastPage}
					order={order}
					page={currentPage}
					rowsPerPage={rowsPerPage}
					setOrder={setOrder}
					setPage={setPage}
					setRowsPerPage={setRowsPerPage}
					totalRows={totalRows}
				/>
			</div>
		</div>
	)
}

export { LuxeSubmissionsMMSubmissions }
